/* eslint-disable no-console */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Field } from 'redux-form';
import { Query } from 'react-apollo';
import { get } from 'lodash';
import {
  required,
  teamRequired,
  applicationTerm,
} from '../../helpers';
import getLabel from '../../helpers/getLabel';
import ApplicationActions from '../ApplicationActions';
import TextField from '../TextField';
import RadioButtonGroup from '../RadioButtonGroup';
import CheckboxGroup from '../CheckboxGroup';
import Checkbox from '../Checkbox';
import SchoolSelectInput from '../SchoolSelectInput';
import TeamMembers from '../TeamMembers';
import { allSchoolsQuery } from '../../apollo';
import sortGradesServed from '../../helpers/sortGradesServed';
import DistrictDropDownMenu from '../DistrictDropDownMenu';
import TextArea from '../TextArea';
import DropDownMenu from '../DropDownMenu';
import CountyList from '../../constants/CountyList';

const StepOneForm = (props) => {
  const {
    data,
    handleSubmit,
    save,
    change,
    formSyncErrors,
    newTeamMember,
    readOnly,
    termsAvailable,
    configBackpackBuddy,
    termsForYear,
    invalid,
    submitFailed,
    enums: {
      gradesServed,
      schoolTypes,
      termTypes,
      backpackBuddyTeamMemberTypes,
    },
  } = props;

  const [schoolSelectionState, setSchoolSelectionState] = useState({
    districtId: null,
    schoolType: null,
    shouldFetch: false,
    hasFetched: false
  });

  const [selectedTerm, setSelectedTerm] = useState(null);
  const [hasExistingApplication, setHasExistingApplication] = useState(false);

  const checkExistingApplication = (selectedSchool) => {
    if (!selectedSchool || !termsForYear.length || !selectedTerm) return false;
    const currentYear = termsForYear[0] && termsForYear[0].year;
    
    const existingApp = selectedSchool.applicationsBackpackBuddy.find(app => {
      if (!app.createdAt) return false;
      return app.term.year == currentYear && app.term.type === selectedTerm.type;
    });
  
    if (existingApp) {
      setHasExistingApplication({
        exists: true,
        status: existingApp.status,
        submittedBy: existingApp.submittedBy
      });
      return true;
    }
  
    setHasExistingApplication(false);
    return false;
  };

  const {
    schoolEnabled,
    summerEnabled,
  } = configBackpackBuddy.configBackpackBuddy;

  const termOptions = termsForYear.filter(el => ((summerEnabled && el.type == 'SUMMER') || (schoolEnabled && el.type == 'SCHOOL')))
  .map(({ termId, type, termFormatted }) => {
    return ({
      value: termId,
      label: (
        <span>
          {getLabel(termTypes.options, type)}
          <span className="term-text">{termFormatted}</span>
        </span>
      ),
    });
  });

  // Get the saved termId for read-only display
  const savedTermId = data && data.applicationBackpackBuddy && data.applicationBackpackBuddy.termId;

  useEffect(() => {
    if (data && data.applicationBackpackBuddy && data.applicationBackpackBuddy.school && 
      data.applicationBackpackBuddy.school.districtId && data.applicationBackpackBuddy.school.type){
      setSchoolSelectionState({
        districtId: data.applicationBackpackBuddy.school.districtId,
        schoolType: data.applicationBackpackBuddy.school.type,
        shouldFetch: false,
        hasFetched: false
      })
    }
  },[data && data.applicationBackpackBuddy && data.applicationBackpackBuddy.school])

  // Set termId value only in read-only mode when we have saved data
  useEffect(() => {
    if (readOnly && savedTermId) {
      change('termId', savedTermId);
    }
  }, [readOnly, savedTermId, change]);

  const handleDistrictChange = (event, newValue) => {
    setSchoolSelectionState(prev => {
      const shouldFetch = Boolean(newValue && prev.schoolType) && !prev.hasFetched;
      return {
        ...prev,
        districtId: newValue,
        shouldFetch: true,
        hasFetched: shouldFetch ? true : prev.hasFetched
      };
    });
    change('school.name', null);
  };

  const handleSchoolTypeChange = (event, newValue) => {
    setSchoolSelectionState(prev => {
      const shouldFetch = Boolean(newValue && prev.districtId) && !prev.hasFetched;
      return {
        ...prev,
        schoolType: newValue,
        shouldFetch: true,
        hasFetched: shouldFetch ? true : prev.hasFetched
      };
    });
    return change('school.name', null);
  };

  const renderSchoolSelect = ({ loading, error, data }) => {
    const schoolOptions = get(data, 'allSchools', []).map(school => ({
      school,
      label: school.name,
    }));
    
    return (
      <Field
        label={<label htmlFor="First" className="field-label">School Name</label>}
        component={SchoolSelectInput}
        options={schoolOptions}
        validate={required}
        districtId={schoolSelectionState.districtId}
        schoolType={schoolSelectionState.schoolType}
        change={change}
        loading={loading}
        name="school.name"
        placeholder={
          loading ? 'Loading schools...' :
          error ? 'Error loading schools' :
          'Select a school'
        }
        className="text-field w-input"
        disabled={readOnly || !termsAvailable || loading}
      />
    );
  };

  return (
    <Form onSubmit={handleSubmit(save)} id="email-form" className="backbackbuddy-form">
      <h4 className="purple-headline boxed-in">Application Type</h4>
      {
        !termsForYear.length ? (
          <span style={{ color: 'red' }}>
            We are not currently accepting applications for this year.
          </span>
        ) : (
          <Field
            name="termId"
            options={termOptions}
            component={RadioButtonGroup}
            className="radio-field w-radio"
            disabled={readOnly || !termsAvailable}
            onChange={(event, newValue) => {
              const term = termsForYear.find(term => term.termId === newValue);
              setSelectedTerm(term);
            }}
            validate={value => applicationTerm(value, termsForYear, summerEnabled, schoolEnabled)}
          />
        )
      }
      {(!(data && data.applicationBackpackBuddy && (data.applicationBackpackBuddy.status === 'APPROVED')) && !termsAvailable && Boolean(termsForYear.length)) && (
        <span style={{ color: 'red', display: 'block' }}>
          We are not currently accepting new applications.
        </span>
      )}
      <h4 className="purple-headline boxed-in">Initial School Information</h4>
      <div className="radio-question">
        <label htmlFor="First-3" className="field-label">Did your site participate in this program last year?</label>
        <Field
          name="new"
          options={[
            {
              value: false,
              label: 'Yes',
            },
            {
              value: true,
              label: 'No',
            },
          ]}
          component={RadioButtonGroup}
          className="radio-field inline-radio w-radio"
          parse={val => val === 'true'}
          disabled={readOnly || !termsAvailable}
          validate={required}
        />
      </div>
      <label htmlFor="First" className="field-label">School Type</label>
      <Field
        name="school.type"
        validate={required}
        options={schoolTypes.options}
        component={RadioButtonGroup}
        className="radio-field inline-radio w-radio"
        disabled={readOnly || !termsAvailable}
        onChange={handleSchoolTypeChange}
      />
      <div className="field-combo">
        <Field
          name="school.districtId"
          className="text-field w-select"
          component={DistrictDropDownMenu}
          label={<label htmlFor="First-3" className="field-label">School District</label>}
          validate={required}
          disabled={readOnly || !termsAvailable}
          onChange={handleDistrictChange}
        />
      </div>
      {(schoolSelectionState.districtId && schoolSelectionState.schoolType) && <>
      <div className="field-combo-wrapper dropdown-select">
        <div className="field-combo">
          <Query query={allSchoolsQuery} 
            variables={{
              districtId: schoolSelectionState.districtId,
              schoolType: schoolSelectionState.schoolType
            }}
            fetchPolicy="network-only">
            {({ loading, error, data }) => {
              const schoolOptions = get(data, 'allSchools', []).map(school => ({
                school,
                label: school.name,
              }));

              return (
                <Field
                  label={<label htmlFor="First-3" className="field-label">School Name</label>}
                  component={SchoolSelectInput}
                  options={schoolOptions}
                  includeTeamMembersBackpackBuddy
                  validate={[required]}
                  change={change}
                  loading={loading}
                  districtId={schoolSelectionState.districtId}
                  schoolType={schoolSelectionState.schoolType}
                  name="school.name"
                  placeholder={error ? 'Error loading schools, please refresh.' : 'Enter School Name'}
                  className="text-field w-input"
                  disabled={readOnly || !termsAvailable}
                  onChangeAux={(value) => {
                    checkExistingApplication(value);
                  }}
                />
              );
            }}
          </Query>
        </div>
        
      </div>
      {!hasExistingApplication && <>
      <div className="textarea-combo">
        <div className="field-combo">
          <Field
            label={<label htmlFor="First-3" className="field-label">Organization Type</label>}
            component={TextField}
            name="organizationType"
            className="text-field w-input"
            placeholder=""
            disabled={readOnly || !termsAvailable}
          />
        </div>
        <div className="field-textarea half">
          <Field
            name="nonProfitOrganizations"
            component={TextArea}
            maxLength={255}
            className="field-textarea w-input"
            label={<label htmlFor="First-3" className="field-label">If you are a 501(c)(3) organization, please list the schools and/or school district (including address), you are partnered with below. If you are a school, please skip this section.</label>}
            disabled={readOnly || !termsAvailable}
          />
        </div>
      </div>
      <div className="field-combo-wrapper">
        <div className="field-combo">
          <Field
            label={<label htmlFor="First-3" className="field-label">Address Line 1</label>}
            component={TextField}
            validate={[required]}
            name="school.address1"
            className="text-field w-input"
            disabled={readOnly || !termsAvailable}
          />
        </div>
        <div className="field-combo">
          <Field
            label={<label htmlFor="Last-3" className="field-label">Address Line 2 (optional)</label>}
            component={TextField}
            name="school.address2"
            className="text-field w-input"
            disabled={readOnly || !termsAvailable}
          />
        </div>
      </div>
      <div className="field-combo-wrapper">
        <div className="field-combo">
          <Field
            label={<label htmlFor="First-3" className="field-label">City</label>}
            component={TextField}
            validate={[required]}
            name="school.city"
            className="text-field w-input"
            disabled={readOnly || !termsAvailable}
          />
        </div>
        <div className="field-combo">
          <Field
            label={<label htmlFor="Last-3" className="field-label">Country</label>}
            component={DropDownMenu}
            validate={[required]}
            name="school.county"
            options={CountyList}
            className="text-field w-input"
            disabled={readOnly || !termsAvailable}
          />
        </div>
      </div>
      <div className="field-combo-wrapper">
        <div className="field-combo">
          <Field
            label={<label htmlFor="First-3" className="field-label">State</label>}
            component={TextField}
            validate={[required]}
            name="school.state"
            className="text-field w-input"
            disabled={readOnly || !termsAvailable}
          />
        </div>
        <div className="field-combo">
          <Field
            label={<label htmlFor="Last-3" className="field-label">Zip</label>}
            component={TextField}
            validate={[required]}
            name="school.zip"
            className="text-field w-input"
            disabled={readOnly || !termsAvailable}
          />
        </div>
      </div>
      <label htmlFor="Last-3" className="field-label">Grades Served (check all that apply)</label>
      <Field
        name="school.gradesServed"
        validate={required}
        options={sortGradesServed(gradesServed)}
        component={CheckboxGroup}
        className="checkbox-inline w-checkbox _7"
        disabled={readOnly || !termsAvailable}
      />
      <h4 className="purple-headline boxed-in">Team Information</h4>
      <p>
        Enter all participants including the principal, coordinators,
        and any staff members or volunteers that will help with the program
        and have access to children on a regular basis.
      </p>
      <br />
      <Field
        name="emailSecondaryCoordinator"
        component={Checkbox}
        className="checkbox-inline w-checkbox"
        label="Include Secondary Coordinator on program communication emails for reports, newsletters and updates."
        disabled={readOnly || !termsAvailable}
      />
      <div className="team-block">
        <h5 className="grey-headline">Manage Program Participants</h5>
        <Field
          name="school.teamMembersBackpackBuddy"
          validate={teamRequired}
          component={TeamMembers}
          change={change}
          newTeamMember={newTeamMember}
          formSyncErrors={formSyncErrors}
          disabled={readOnly || !termsAvailable}
          teamMemberTypes={backpackBuddyTeamMemberTypes}
        />
      </div>
      </>}
      </>}
      {hasExistingApplication && (
        <div style={{ 
          color: '#664d03', 
          padding: '15px', 
          margin: '20px 0', 
          border: '1px solid #ffecb5',
          borderRadius: '4px',
          backgroundColor: '#fff3cd'
        }}>
          <p style={{ margin: '0 0 10px 0' }}>
            This school already has a Backpack Buddy application in progress for {termsForYear[0] && termsForYear[0].year}.
          </p>
          <p style={{ margin: '0' }}>
              In Progress Application Started By: 
              <a href={`mailto:${hasExistingApplication.submittedBy}`} className="paragraph-link">
                {hasExistingApplication.submittedBy}
              </a>
          </p>
          If assistance is needed, please contact:
          <a href={`mailto:backpackbuddy@houstonfoodbank.org`} className="paragraph-link">
          backpackbuddy@houstonfoodbank.org
          </a>
        </div>
      )}
      {!hasExistingApplication && <ApplicationActions {...props} />}
      {
        (invalid && submitFailed) && (
          <span
            style={{
              color: 'red',
            }}
          >
            You have required field(s) that are missing. Please see above.
          </span>
        )
      }
    </Form>
  );
};

StepOneForm.propTypes = {
  adminView: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  save: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  termsAvailable: PropTypes.bool.isRequired,
  change: PropTypes.func.isRequired,
  formSyncErrors: PropTypes.object.isRequired,
  enums: PropTypes.object.isRequired,
  newTeamMember: PropTypes.object,
  user: PropTypes.object.isRequired,
  configBackpackBuddy: PropTypes.object.isRequired,
  termsForYear: PropTypes.array.isRequired,
  invalid: PropTypes.bool.isRequired,
  submitFailed: PropTypes.bool.isRequired,
  data: PropTypes.object,
};

StepOneForm.defaultProps = {
  readOnly: false,
  newTeamMember: null,
  data: null,
};

export default StepOneForm;
